import { FunctionComponent, SVGProps } from 'react';
import OpcoUsersIcon from '../images/OpcoUsersIcon.svg?react';
import BagIcon from '../images/BagIcon.svg?react';
import BillingIcon from '../images/BillingIcon.svg?react';
import DepositAccountsIcon from '../images/DepositAccountsIcon.svg?react';
import OrganizationDetailsIcon from '../images/OrganizationDetailsIcon.svg?react';
import OrganizationBillingIcon from '../images/OrganizationBillingIcon.svg?react';
import DevelopersIcon from '../images/DevelopersIcon.svg?react';
import { getOpcoOrganizationsPath } from '../../_Opco/path/path';
import { getRecipientWiresPath } from '../../_Recipient/path/path';
import { getExecutorWiresPath } from '../../_Executor/path/path';
import { getOutboundWireDetailsPath } from '../../_OrgAdmin/pages/OutboundWire/path/path';
import { getRecipientWireDetailsPath } from '../../_Recipient/_Details/path/path';
import { getDepositAccountsPath } from '@/bundle/_OrgAdmin/pages/DepositAccountsPage/path/path';
import { getBilledEventsPath } from '@/bundle/_Opco/_BilledEventsPage/path/path';
import { getCreateDepositAccountPath } from '@/bundle/_OrgAdmin/pages/DepositAccountsPage/_CreateDepositAccountPage/path/path';
import { getDepositAccountDetailsPath } from '@/bundle/_OrgAdmin/pages/DepositAccountsPage/_DepositAccountDetailsPage/path/path';
import { getInboundWireDetailsPath } from '../../_OrgAdmin/pages/InboundWire/path/path';
import { USER_ROLE_MAP } from '@/const/user';
import { getOpcoUsersPath } from '@/bundle/_Opco/_OpcoUsersPage/path/path';
import { getOpcoUserDetailsPath } from '@/bundle/_Opco/_OpcoUsersPage/OpcoUserDetailsPage/path/path';
import { getOpcoOrganizationUsersPath } from '@/bundle/_Opco/ui/OrganizationDetails/path/path';
import { getOrganizationUserDetailsPath } from '@/bundle/_Opco/ui/OrganizationDetails/Details/path/path';
import { useUser } from '@/context/userContext';
import { getDepositorWiresPath } from '@/bundle/_Depositor/path/path';
import { getMasterAdminOrganizationDetailsPath } from '@/bundle/_MasterAdmin/path/path';
import { getMasterAdminBillingPath } from '@/bundle/_MasterAdmin/_BillingPage/path/path';
import { getMasterAdminOrganizationUserDetailsPath } from '@/bundle/_MasterAdmin/_OrganizationUserDetailsPage/path/path';
import { getMasterAdminBillingEventsPath } from '@/bundle/_MasterAdmin/_BillingPage/_BillingEventsPage/path/path';
import { SelfUserDetailsType } from '@/api/v1/users/getSelfUserDetailsApi';
import { getTransactionsPath } from '@/bundle/_OrgAdmin/path/path';
import { getTransactionDetailsPath } from '@/bundle/_OrgAdmin/pages/TransactionDetailsPage/path/path';
import { getDepositorWireDetailsPath } from '@/bundle/_Depositor/_DepositorWireDetailsPage/path/path';
import { getExecutorWireDetailsPath } from '@/bundle/_Executor/_ExecutorWireDetailsPage/path/path';
import { getDevelopersPath } from '@/bundle/_MasterAdmin/_DevelopersPage/path/path';
import { hasBillingSubscription } from '@/helpers/billingHelpers';
import { ONBOARDING_GUIDE_STEP_ID } from '@/context/OnboardingGuideContext/const';
import { getMyVaultPath } from '@/bundle/_OrgAdmin/pages/MyVaultPage/path/path';
import { getVaultAccountDetailsPath } from '@/bundle/_OrgAdmin/pages/MyVaultPage/_MyVaultAccountDetailsPage/path/path';
import VaultIcon from '@/images/VaultIcon.svg?react';
import VendorVaultIcon from '@/images/VendorVaultIcon.svg?react';
import { getVendorVaultPath } from '@/bundle/_OrgAdmin/pages/VendorVaultPage/path/path';
import AuditLogIcon from '@/images/AuditLogIcon.svg?react';
import DashboardIcon from '@/images/DashboardIcon.svg?react';
import { getAuditLogPath } from '@/bundle/_OrgAdmin/pages/AuditLogPage/path/path';
import { getDashboardPath } from '@/bundle/_OrgAdmin/pages/DashboardPage/path/path';
import { getAuditLogEventDetailsPath } from '@/bundle/_OrgAdmin/pages/AuditLogPage/_AuditLogEventDetailsPage/path/path';
import { getInvoiceVaultPath } from '@/bundle/_OrgAdmin/pages/InvoiceVaultPage/path/path';
import InvoiceVaultIcon from '@/images/InvoiceVaultIcon.svg?react';

export type SidebarMenuItemType = {
  title: string;
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  url: string;
  paths?: string[];
  isVisibleMenuItem?: (userDetails: SelfUserDetailsType) => boolean;
  id?: string;
  separator?: boolean;
};

type SidebarMenuType = {
  menu: SidebarMenuItemType[];
};

type SideBarMenuMapType = {
  [key: string]: SidebarMenuType;
};

const OPCO_MENU: SidebarMenuType = {
  menu: [
    {
      title: 'OpCo Users',
      icon: OpcoUsersIcon,
      url: getOpcoUsersPath(),
      paths: [getOpcoUsersPath(), getOpcoUserDetailsPath()],
    },
    {
      title: 'Org Management',
      icon: BagIcon,
      url: getOpcoOrganizationsPath(),
      paths: [getOpcoOrganizationsPath(), getOpcoOrganizationUsersPath(), getOrganizationUserDetailsPath()],
    },
    {
      title: 'Billing',
      icon: BillingIcon,
      url: getBilledEventsPath(),
      paths: [getBilledEventsPath()],
    },
  ],
};

const ORG_ADMIN_MENU: SidebarMenuType = {
  menu: [
    {
      title: 'Dashboard',
      icon: DashboardIcon,
      url: getDashboardPath(),
      paths: [getDashboardPath()],
    },
    {
      title: 'My Vault',
      icon: VaultIcon,
      url: getMyVaultPath(),
      paths: [getMyVaultPath(), getVaultAccountDetailsPath()],
      id: ONBOARDING_GUIDE_STEP_ID.DEPOSIT_ACCOUNTS,
    },
    {
      title: 'Vendor Vault',
      icon: VendorVaultIcon,
      url: getVendorVaultPath(),
      paths: [getVendorVaultPath()],
    },
    {
      title: 'Invoice Vault',
      icon: InvoiceVaultIcon,
      url: getInvoiceVaultPath(),
      paths: [getInvoiceVaultPath()],
    },
    {
      title: 'Audit log',
      icon: AuditLogIcon,
      url: getAuditLogPath(),
      paths: [getAuditLogPath(), getAuditLogEventDetailsPath()],
    },
    {
      title: '',
      icon: null,
      url: '',
      separator: true,
    },
    {
      title: 'Transactions',
      icon: BagIcon,
      url: getTransactionsPath(),
      paths: [
        getTransactionsPath(),
        getTransactionDetailsPath(),
        getOutboundWireDetailsPath(),
        getInboundWireDetailsPath(),
      ],
    },
    {
      title: 'Receiving Accounts',
      icon: DepositAccountsIcon,
      url: getDepositAccountsPath(),
      paths: [getDepositAccountsPath(), getCreateDepositAccountPath(), getDepositAccountDetailsPath()],
      id: ONBOARDING_GUIDE_STEP_ID.DEPOSIT_ACCOUNTS,
    },
  ],
};

const MASTER_ADMIN_MENU: SidebarMenuType = {
  menu: [
    ...ORG_ADMIN_MENU.menu,
    {
      title: 'Org Details',
      icon: OrganizationDetailsIcon,
      url: getMasterAdminOrganizationDetailsPath(),
      paths: [getMasterAdminOrganizationDetailsPath(), getMasterAdminOrganizationUserDetailsPath()],
    },
    {
      title: 'Billing',
      icon: OrganizationBillingIcon,
      url: getMasterAdminBillingPath(),
      paths: [getMasterAdminBillingPath(), getMasterAdminBillingEventsPath()],
      isVisibleMenuItem(userDetails: SelfUserDetailsType) {
        return hasBillingSubscription(userDetails);
      },
    },
    {
      title: 'Developers',
      icon: DevelopersIcon,
      url: getDevelopersPath(),
      paths: [getDevelopersPath()],
      isVisibleMenuItem(userDetails: SelfUserDetailsType) {
        return !!userDetails?.organization_user?.organization?.is_api_keys_allowed;
      },
    },
  ],
};

const RECIPIENT_MENU: SidebarMenuType = {
  menu: [
    {
      title: 'Wires',
      icon: BagIcon,
      url: getRecipientWiresPath(),
      paths: [getRecipientWiresPath(), getRecipientWireDetailsPath()],
    },
  ],
};

const EXECUTOR_MENU: SidebarMenuType = {
  menu: [
    {
      title: 'Wires',
      icon: BagIcon,
      url: getExecutorWiresPath(),
      paths: [getExecutorWiresPath(), getExecutorWireDetailsPath()],
    },
  ],
};

const DEPOSITOR_MENU: SidebarMenuType = {
  menu: [
    {
      title: 'Wires',
      icon: BagIcon,
      url: getDepositorWiresPath(),
      paths: [getDepositorWiresPath(), getDepositorWireDetailsPath()],
    },
  ],
};

const USER_SIDEBAR_MENU_MAP: SideBarMenuMapType = {
  [USER_ROLE_MAP.OPCO]: OPCO_MENU,
  [USER_ROLE_MAP.ORG_ADMIN]: ORG_ADMIN_MENU,
  [USER_ROLE_MAP.MASTER_ADMIN]: MASTER_ADMIN_MENU,
  [USER_ROLE_MAP.RECIPIENT]: RECIPIENT_MENU,
  [USER_ROLE_MAP.EXECUTOR]: EXECUTOR_MENU,
  [USER_ROLE_MAP.DEPOSITOR]: DEPOSITOR_MENU,
};

export const useSidebar = () => {
  const { currentRole, user } = useUser();

  if (!currentRole) return { menu: [] };

  const userMenu = USER_SIDEBAR_MENU_MAP[currentRole] || { menu: [] };
  // Filter out menu items that might be hidden by some conditions.
  const visibleMenu = userMenu.menu.filter((menuItem) => {
    if ('type' in menuItem && menuItem.type === 'separator') return true;

    if (!(menuItem as SidebarMenuItemType).isVisibleMenuItem) return true;

    return (menuItem as SidebarMenuItemType).isVisibleMenuItem(user);
  });

  return { menu: visibleMenu };
};
